import type { IAuthRefusal } from "./interfaces";

/**
 * Auth0's wording for a login refused on organization membership, measured
 * against davaco-dev on 2026-09-27:
 *
 * @example
 *   error=access_denied
 *   error_description=user google-oauth2|113462336056896342775 is not part of
 *   the org_b5BIbCvnpmk0znCN organization
 */
const NOT_A_MEMBER = /^user (\S+) is not part of the \S+ organization/i;

/**
 * Reads an Auth0 error off the callback querystring, or null when there is none.
 *
 * Needed because an error callback carries `error` and `state`, never `code`,
 * and the provider only looked for `code`. The error was ignored, the visitor
 * had no session, the guard sent them to /login, and on a customer host /login
 * hands straight back to Auth0 — which refused the same account again. A
 * non-member was bounced between the two until Auth0 gave up.
 */
export function readAuthRefusal(search: string): IAuthRefusal | null {
  const params = new URLSearchParams(search);
  const error: string | null = params.get("error");

  if (error === null || error.length === 0) {
    return null;
  }

  const description: string = params.get("error_description") ?? error;
  const member: RegExpExecArray | null =
    error === "access_denied" ? NOT_A_MEMBER.exec(description) : null;

  return member === null
    ? { kind: "error", subject: null, description }
    : { kind: "not-a-member", subject: member[1] ?? null, description };
}
